import { useState } from 'react';

import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import { Button, Checkbox, Select } from 'components/FormComponents';

import {
  disableActiveDirectorySync,
  getActiveDirectorySyncConfig,
  selectCompany,
  updateCompany,
} from 'api/company-slice';

export const ActiveDirectoryConfig = ({ setLoading = () => {} }) => {
  const dispatch = useDispatch();
  const company = useSelector(selectCompany);

  const [enabled, setEnabled] = useState(!!company?.ad_sync_directory_id);
  const [deprovisionAction, setDeprovisionAction] = useState(
    company?.ad_sync_deprovision_action || 'suspend',
  );
  const [allowManualUserCreation, setAllowManualUserCreation] = useState(
    company?.ad_sync_allow_manual_user_creation ?? true,
  );

  const onToggle = async e => {
    if (e.target.checked) {
      setEnabled(true);
      return;
    }

    setEnabled(false);
    setLoading(true);
    await dispatch(disableActiveDirectorySync());
    setLoading(false);
  };

  const onDeprovisionActionChange = async e => {
    const value = e.target.value;
    setDeprovisionAction(value);
    await dispatch(
      updateCompany({ id: company.id, params: { ad_sync_deprovision_action: value } }),
    );
  };

  const onAllowManualUserCreationChange = async checked => {
    setAllowManualUserCreation(checked);
    await dispatch(
      updateCompany({ id: company.id, params: { ad_sync_allow_manual_user_creation: checked } }),
    );
  };

  const onConfigure = async () => {
    setLoading(true);
    const { payload } = await dispatch(getActiveDirectorySyncConfig());
    setEnabled(!!payload.company.ad_sync_directory_id);
    window.open(payload.portal_link);
    setLoading(false);
  };

  return (
    <>
      <div className={cn('background-secondary', 'u-padding')}>
        <div className={cn('card', 'card-with-border', 'u-padding')}>
          <EditableSettings
            title="Active Directory Sync Enabled"
            subtitle="Connect your Azure AD, Okta, or Google Workspace directory to provision and de-provision Checkmate users automatically."
            name="ad_sync_directory_id"
            open={enabled}
            toggleable
            hideCollapseChevron
            onChange={onToggle}>
            <p className="t-small secondary-text">
              Once connected, map directory groups to Checkmate roles and teams from the{' '}
              <strong>Group Mapping</strong> tab under Users.
            </p>
            <div className="u-margin-top">
              <p className="t-small u-margin-bottom--small">
                When someone is removed or suspended in your directory, their Checkmate account
                should be:
              </p>
              <Select
                inputProps={{ value: deprovisionAction, onChange: onDeprovisionActionChange }}
                value={deprovisionAction}>
                <Select.Item value="suspend">Suspended (recommended)</Select.Item>
                <Select.Item value="delete">Deleted</Select.Item>
              </Select>
            </div>
            <div className="u-margin-top">
              <Checkbox
                checked={allowManualUserCreation}
                onChange={onAllowManualUserCreationChange}
                label="Allow users to also be manually invited from the Users page"
              />
            </div>
          </EditableSettings>
        </div>
      </div>
      <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-end')}>
        <Button onClick={onConfigure}>{enabled ? 'Reconfigure' : 'Configure'}</Button>
      </div>
    </>
  );
};
